<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useEvolutionStatusPoll } from 'dashboard/composables/useEvolutionStatusPoll';
import EvolutionAPI from 'dashboard/api/evolution';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();

const {
  status,
  phoneNumber,
  qrCode,
  disconnectReason,
  lastError,
  isPolling,
  startPolling,
} = useEvolutionStatusPoll(props.inbox.id);

const STATUS_LABELS = {
  connecting: 'CONNECTING',
  qr_pending: 'QR_PENDING',
  open: 'OPEN',
  closed: 'CLOSED',
  disconnected: 'DISCONNECTED',
  error: 'ERROR',
};

const STATUS_CLASSES = {
  connecting: 'bg-n-slate-3 text-n-slate-11',
  qr_pending: 'bg-n-amber-3 text-n-amber-11',
  open: 'bg-n-teal-3 text-n-teal-11',
  closed: 'bg-n-ruby-3 text-n-ruby-11',
  disconnected: 'bg-n-ruby-3 text-n-ruby-11',
  error: 'bg-n-ruby-3 text-n-ruby-11',
};

const statusLabel = computed(() =>
  t(
    `INBOX_MGMT.EVOLUTION.STATUS.${STATUS_LABELS[status.value] || 'CONNECTING'}`
  )
);
const statusClass = computed(
  () => STATUS_CLASSES[status.value] || STATUS_CLASSES.connecting
);
const isOpen = computed(() => status.value === 'open');
const showQr = computed(() => status.value === 'qr_pending' && qrCode.value);

const reconnect = async () => {
  try {
    await EvolutionAPI.reconnect(props.inbox.id);
    startPolling();
    useAlert(t('INBOX_MGMT.EVOLUTION.RECONNECT_SUCCESS'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.EVOLUTION.RECONNECT_ERROR'));
  }
};

const disconnect = async () => {
  try {
    await EvolutionAPI.disconnect(props.inbox.id);
    startPolling();
    useAlert(t('INBOX_MGMT.EVOLUTION.DISCONNECT_SUCCESS'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.EVOLUTION.DISCONNECT_ERROR'));
  }
};

const updateToggle = async (key, value) => {
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      channel: { [key]: value },
    });
  } catch (error) {
    useAlert(t('INBOX_MGMT.EVOLUTION.TOGGLE_ERROR'));
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      class="flex items-center justify-between p-4 border rounded-xl border-n-weak bg-n-solid-1"
    >
      <div class="flex items-center gap-3">
        <span
          class="px-2 py-0.5 rounded-full text-xs font-semibold"
          :class="statusClass"
        >
          {{ statusLabel }}
        </span>
        <span v-if="isOpen && phoneNumber" class="text-sm text-n-slate-12">
          {{ phoneNumber }}
        </span>
        <span v-else-if="isPolling" class="text-xs text-n-slate-10">
          {{ t('INBOX_MGMT.EVOLUTION.POLLING') }}
        </span>
      </div>
      <div class="flex gap-2">
        <NextButton
          v-if="!isOpen"
          faded
          slate
          sm
          :label="t('INBOX_MGMT.EVOLUTION.RECONNECT')"
          @click="reconnect"
        />
        <NextButton
          v-else
          faded
          slate
          sm
          class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
          :label="t('INBOX_MGMT.EVOLUTION.DISCONNECT')"
          @click="disconnect"
        />
      </div>
    </div>

    <p v-if="!isOpen && lastError" class="text-xs text-n-ruby-11 -mt-2">
      {{ lastError }}
    </p>
    <p
      v-else-if="!isOpen && disconnectReason"
      class="text-xs text-n-ruby-11 -mt-2"
    >
      {{
        t('INBOX_MGMT.EVOLUTION.DISCONNECT_REASON', {
          reason: disconnectReason,
        })
      }}
    </p>

    <div
      v-if="showQr"
      class="flex flex-col items-center gap-2 p-6 border rounded-xl border-n-weak bg-n-solid-1"
    >
      <p class="text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.EVOLUTION.SCAN_QR') }}
      </p>
      <img :src="qrCode" alt="QR code" class="rounded-lg size-56" />
    </div>

    <SettingsToggleSection
      :header="t('INBOX_MGMT.EVOLUTION.SYNC_CONTACTS.LABEL')"
      :description="t('INBOX_MGMT.EVOLUTION.SYNC_CONTACTS.HELP')"
      :model-value="!!inbox.sync_contacts"
      @update:model-value="value => updateToggle('sync_contacts', value)"
    />
    <SettingsToggleSection
      :header="t('INBOX_MGMT.EVOLUTION.SYNC_MESSAGES.LABEL')"
      :description="t('INBOX_MGMT.EVOLUTION.SYNC_MESSAGES.HELP')"
      :model-value="!!inbox.sync_messages"
      @update:model-value="value => updateToggle('sync_messages', value)"
    />
    <SettingsToggleSection
      :header="t('INBOX_MGMT.EVOLUTION.SYNC_OTHER_DEVICES.LABEL')"
      :description="t('INBOX_MGMT.EVOLUTION.SYNC_OTHER_DEVICES.HELP')"
      :model-value="!!inbox.sync_other_devices"
      @update:model-value="value => updateToggle('sync_other_devices', value)"
    />
    <SettingsToggleSection
      :header="t('INBOX_MGMT.EVOLUTION.MEDIA_ENABLED.LABEL')"
      :description="t('INBOX_MGMT.EVOLUTION.MEDIA_ENABLED.HELP')"
      :model-value="!!inbox.media_enabled"
      @update:model-value="value => updateToggle('media_enabled', value)"
    />
  </div>
</template>
